import type { BillingSqlExecutor } from "../subject/repository.ts";
import type { AthenaPostgresPool } from "../../postgres/driver.ts";

export interface BillingImportDatabase extends BillingSqlExecutor {
	transaction<T>(fn: (tx: BillingSqlExecutor) => Promise<T>): Promise<T>;
}

export function createBillingSqlExecutor(input: {
	query: (
		text: string,
		values?: unknown[],
	) => Promise<{ rows: unknown[]; rowCount?: number | null }>;
}): BillingSqlExecutor {
	return {
		async query(sql, params) {
			const result = await input.query(sql, params ? [...params] : undefined);
			return { rows: result.rows as Record<string, unknown>[] };
		},
	};
}

export function createBillingImportDatabase(
	pool: AthenaPostgresPool,
): BillingImportDatabase {
	const root = createBillingSqlExecutor(pool);
	return {
		query: root.query.bind(root),
		async transaction(fn) {
			const client = await pool.connect();
			try {
				await client.query("BEGIN");
				const tx = createBillingSqlExecutor(client);
				const value = await fn(tx);
				await client.query("COMMIT");
				return value;
			} catch (error) {
				try {
					await client.query("ROLLBACK");
				} catch {
					// ignore rollback failure; original error is authoritative
				}
				throw error;
			} finally {
				client.release();
			}
		},
	};
}

export function wrapBillingSqlExecutorAsDatabase(
	sql: BillingSqlExecutor,
): BillingImportDatabase {
	return {
		query: sql.query.bind(sql),
		async transaction(fn) {
			return fn(sql);
		},
	};
}
